import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Modal } from '../common/Modal';
import { RoastProfileSection } from '../../pages/roasts/component/RoastProfile';
import { RoastResultSection } from '../../pages/roasts/component/RoastResultSection';
import type { RoastProfileRow, RoastStageState } from '../../pages/roasts/component/types';
import { useSettingsStore } from '../../store/settingsStore';
import { useWeightUnit } from '../../hooks/useWeightUnit';
import { workOrderRecipeService, type WorkOrderRecipeDTO, type InventoryRoastProfileSavePayload } from '../../services/workOrderRecipeService';
import { useLanguage } from '../../features/language/useLanguage';
import { invalidateRoastProfileExistence } from '../../lib/roastProfileQueries';

interface InventoryRoastProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  workOrderId?: string;
  referenceId?: string;
  onSaved?: () => void;
  readOnly?: boolean;
}

const defaultStages: RoastStageState[] = [
  { key: 'start', label: 'Start', time: '00:00', temp: '' },
  { key: 'turning_point', label: 'Turning Point', time: '', temp: '' },
  { key: 'yellow', label: 'Yellow', time: '', temp: '' },
  { key: 'first_crack', label: 'First Crack', time: '', temp: '' },
  { key: 'end', label: 'End', time: '', temp: '' },
];

export const InventoryRoastProfileModal: React.FC<InventoryRoastProfileModalProps> = ({
  isOpen,
  onClose,
  title,
  workOrderId,
  referenceId,
  onSaved,
  readOnly,
}) => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const { defaultUnit } = useSettingsStore();
  const { unit, toggleUnit } = useWeightUnit(defaultUnit as 'kg' | 'gram');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recipeId, setRecipeId] = useState<string | null>(null);
  const [profile, setProfile] = useState<RoastProfileRow[]>([]);
  const [stageStates, setStageStates] = useState<RoastStageState[]>(defaultStages);
  const [weightInput, setWeightInput] = useState<number>(0);
  const [weightOutput, setWeightOutput] = useState<number | ''>('');
  const [agtronWhole, setAgtronWhole] = useState<number | ''>('');
  const [agtronGround, setAgtronGround] = useState<number | ''>('');
  const [roastType, setRoastType] = useState<string>('');

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingPayload, setPendingPayload] = useState<InventoryRoastProfileSavePayload | null>(null);
  const initialPayloadRef = useRef<InventoryRoastProfileSavePayload | null>(null);

  const hasWorkOrder = !!workOrderId;

  const resetState = useCallback(() => {
    setRecipeId(null);
    setProfile([]);
    setStageStates(defaultStages);
    setWeightInput(0);
    setWeightOutput('');
    setAgtronWhole('');
    setAgtronGround('');
    setRoastType('');
    setError(null);
    setConfirmOpen(false);
    setPendingPayload(null);
    initialPayloadRef.current = null;
  }, []);

  const mapDtoToState = useCallback((dto: WorkOrderRecipeDTO | null) => {
    if (!dto) {
      resetState();
      return;
    }

    setRecipeId(dto.id);

    const inputWeight = dto.input_weight ?? 0;
    const outputWeight = dto.output_weight ?? 0;

    setWeightInput(inputWeight);
    setWeightOutput(outputWeight || '');

    if (dto.agtron) {
      setAgtronWhole(dto.agtron.wholeBean ?? '');
      setAgtronGround(dto.agtron.groundBean ?? '');
    } else {
      setAgtronWhole('');
      setAgtronGround('');
    }

    setRoastType(dto.roast_type ?? '');

    const stagesSummary = dto.stage_summary ?? {};
    const mappedStageStates = defaultStages.map((s) => {
      const summary = stagesSummary[s.key] as { time?: string; temp?: number | string } | undefined;
      const rawTemp = summary?.temp;
      const numericTemp = typeof rawTemp === 'string' ? Number(rawTemp) : rawTemp;
      const safeTemp = numericTemp === undefined || Number.isNaN(numericTemp) ? s.temp : numericTemp;

      return {
        ...s,
        time: summary?.time ?? s.time,
        temp: safeTemp,
      };
    });
    setStageStates(mappedStageStates);

    if (dto.stages && Array.isArray(dto.stages)) {
      const mappedProfile: RoastProfileRow[] = dto.stages.map((stage) => {
        const timeMs = (stage.time_seconds || 0) * 1000;
        const timeStr = new Date(timeMs).toISOString().substr(14, 5);
        return {
          time: timeStr,
          seconds: stage.time_seconds || 0,
          bean: stage.temperature,
          ror: null,
          fire: stage.fire ?? null,
          airflow: stage.airflow ?? null,
          airTemp: stage.air_temp ?? null,
          drumTemp: stage.drum_temp ?? null,
          environmentTemp: stage.environment_temp ?? null,
          drumRpm: stage.drum_rpm ?? null,
        };
      });
      setProfile(mappedProfile);
    } else {
      setProfile([]);
    }
  }, [resetState]);

  useEffect(() => {
    if (!isOpen) return;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        let dto: WorkOrderRecipeDTO | null = null;
        if (hasWorkOrder && workOrderId) {
          dto = await workOrderRecipeService.getByWorkOrderId(workOrderId);
        } else if (referenceId) {
          dto = await workOrderRecipeService.getByReferenceId(referenceId);
        }
        mapDtoToState(dto);
      } catch {
        setError(t('inventory.roastProfileModal.errors.loadFailed'));
        resetState();
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [isOpen, hasWorkOrder, workOrderId, referenceId, mapDtoToState, resetState, t]);

  const buildPayload = useCallback((): InventoryRoastProfileSavePayload => {
    const stageSummary = stageStates.reduce(
      (acc, s) => ({
        ...acc,
        [s.key]: { time: s.time, temp: s.temp },
      }),
      {} as Record<string, { time?: string; temp?: string | number }>,
    );

    const parseTimeToMinutes = (value: string | undefined) => {
      if (!value) return 0;
      const parts = value.split(':');
      if (parts.length !== 2) return 0;
      const minutes = Number(parts[0]);
      const seconds = Number(parts[1]);
      if (Number.isNaN(minutes) || Number.isNaN(seconds)) return 0;
      return minutes + seconds / 60;
    };

    const startTime = stageStates.find((s) => s.key === 'start')?.time;
    const endTime = stageStates.find((s) => s.key === 'end')?.time;
    const totalTimeMinutes = Math.max(parseTimeToMinutes(endTime) - parseTimeToMinutes(startTime), 0);

    const numericInputWeight = Number(weightInput || 0);
    const numericOutputWeight = Number(weightOutput || 0);

    const shrinkagePercent =
      numericInputWeight > 0 && numericOutputWeight > 0
        ? ((numericInputWeight - numericOutputWeight) / numericInputWeight) * 100
        : 0;

    const payload: InventoryRoastProfileSavePayload = {
      workOrderId,
      referenceId,
      roastType: roastType || undefined,
      weightInput: numericInputWeight,
      weightOutput: numericOutputWeight || undefined,
      agtron:
        agtronWhole || agtronGround
          ? {
              wholeBean: typeof agtronWhole === 'string' ? Number(agtronWhole) : agtronWhole,
              groundBean: typeof agtronGround === 'string' ? Number(agtronGround) : agtronGround,
            }
          : undefined,
      stageSummary,
      profile,
      weightAnalysis: {
        shrinkagePercent,
        totalTimeMinutes,
        inputWeight: numericInputWeight,
        outputWeight: numericOutputWeight || undefined,
      },
    };

    return payload;
  }, [
    agtronGround,
    agtronWhole,
    profile,
    referenceId,
    roastType,
    stageStates,
    weightInput,
    weightOutput,
    workOrderId,
  ]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (!recipeId) {
      return;
    }
    if (loading) {
      return;
    }
    if (initialPayloadRef.current) {
      return;
    }
    initialPayloadRef.current = buildPayload();
  }, [isOpen, recipeId, loading, buildPayload]);

  const handleProfileChange = useCallback((rows: RoastProfileRow[]) => {
    setProfile(rows);
  }, []);

  const handleStageChange = useCallback(
    (index: number, field: keyof RoastStageState, value: string | number) => {
      setStageStates((prev) => {
        const next = [...prev];
        next[index] = { ...next[index], [field]: value };
        return next;
      });
    },
    [],
  );

  const calculatedWeight = useMemo(() => {
    return weightInput || 0;
  }, [weightInput]);

  const performSave = async (payload: InventoryRoastProfileSavePayload) => {
    setSaving(true);
    setError(null);

    try {
      let result: WorkOrderRecipeDTO;
      if (recipeId) {
        result = await workOrderRecipeService.update(recipeId, payload);
      } else {
        result = await workOrderRecipeService.create(payload);
      }

      setRecipeId(result.id);
      // Every Roast Profile button on screen is rendering a cached answer to "does this
      // item have a profile?", and this save is what just made those answers wrong. Done
      // here rather than in each onSaved so no caller can forget it (INV-021).
      invalidateRoastProfileExistence(queryClient);
      if (onSaved) {
        onSaved();
      }
      onClose();
    } catch {
      setError(t('inventory.roastProfileModal.errors.saveFailed'));
    } finally {
      setSaving(false);
      setConfirmOpen(false);
      setPendingPayload(null);
    }
  };

  const handleSave = () => {
    const payload = buildPayload();

    if (recipeId && initialPayloadRef.current) {
      const initialSerialized = JSON.stringify(initialPayloadRef.current);
      const currentSerialized = JSON.stringify(payload);
      if (initialSerialized !== currentSerialized) {
        setPendingPayload(payload);
        setConfirmOpen(true);
        return;
      }
    }

    void performSave(payload);
  };

  const handleConfirmProceed = () => {
    if (!pendingPayload) {
      setConfirmOpen(false);
      return;
    }
    void performSave(pendingPayload);
  };

  const handleConfirmCancel = () => {
    setConfirmOpen(false);
    setPendingPayload(null);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      maxWidth="6xl"
    >
      <div
        data-test-id="inventory-roast-profile-modal"
        className="space-y-4"
      >
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : (
          <>
            {error && (
              <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <div className={readOnly ? "pointer-events-none opacity-90" : ""}>
              {roastType && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {roastType}
                </span>
              )}
              <RoastProfileSection
                key={stageStates?.map(s => s.time).join('-') || 'profile-section-inventory'}
                seconds={0}
                stage="idle"
                onDataChange={readOnly ? () => {} : handleProfileChange}
                mode="recipe"
                initialProfile={profile}
                stackLayout
                preserveHeightGroupId="inventory-roast-profile"
                stages={stageStates || []}
              />

              <RoastResultSection
                stages={stageStates}
                onStageChange={readOnly ? () => {} : handleStageChange}
                weightInput={weightInput}
                calculatedWeight={calculatedWeight}
                onWeightInputChange={(value) => !readOnly && setWeightInput(Number(value || 0))}
                weightOutput={weightOutput === '' ? undefined : weightOutput}
                onWeightOutputChange={(value) =>
                  !readOnly && setWeightOutput(value === undefined ? '' : value)
                }
                agtronWhole={agtronWhole === '' ? '' : String(agtronWhole)}
                agtronGround={agtronGround === '' ? '' : String(agtronGround)}
                onAgtronChange={(field, value) => {
                  if (readOnly) return;
                  if (field === 'whole') {
                    setAgtronWhole(value === '' ? '' : Number(value));
                  } else {
                    setAgtronGround(value === '' ? '' : Number(value));
                  }
                }}
                mode="recipe"
                roastType={roastType}
                onRoastTypeChange={readOnly ? () => {} : setRoastType}
                compactLayout
                unit={unit}
                onToggle={toggleUnit}
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                type="button"
                data-test-id="inventory-roast-profile-cancel-button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                {readOnly ? 'Close' : 'Cancel'}
              </button>
              {!readOnly && (
                <button
                  type="button"
                  data-test-id="inventory-roast-profile-save-button"
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              )}
            </div>
          </>
        )}
      </div>
      <Modal
        isOpen={confirmOpen}
        onClose={handleConfirmCancel}
        title={t('inventory.modals.confirmUpdateData')}
        maxWidth="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            This update will affect work orders, inventory levels, stock movement history, and analytics, potentially impacting traceability.
          </p>
          <p className="text-sm text-gray-700">
            Please review your changes carefully before proceeding.
          </p>
          <div className="flex justify-end space-x-3 pt-2">
            <button
              type="button"
              onClick={handleConfirmCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirmProceed}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700"
            >
              Proceed to save
            </button>
          </div>
        </div>
      </Modal>
    </Modal>
  );
};
